import * as React from "react"
import { navigate } from "gatsby"
import { trackEvent } from "../utils/trackEvent"
import { useLeadForm } from "./LeadFormContext"
import { signupCtaMode, spotApiConfigured } from "../utils/flags"

/**
 * Routes the top-level "Enable Your Portfolio" CTA. Behind the
 * GATSBY_SIGNUP_CTA_MODE flag:
 * - "signup" (default): navigates straight to the /signup flow (falls back to
 *   the lead form when spot-api isn't configured for this build).
 * - "choice": opens the Sign Up / Learn More chooser modal.
 * - "legacy": delegates straight to the existing lead form modal.
 */
type GetStartedContextType = {
  isChoiceOpen: boolean
  open: (source?: string) => void
  closeChoice: () => void
}

const GetStartedContext = React.createContext<GetStartedContextType | undefined>(undefined)

export const GetStartedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isChoiceOpen, setIsChoiceOpen] = React.useState(false)
  const leadForm = useLeadForm()

  const open = React.useCallback(
    (source?: string) => {
      if (signupCtaMode === "legacy" || (signupCtaMode === "signup" && !spotApiConfigured)) {
        leadForm.open(source)
        return
      }
      if (signupCtaMode === "signup") {
        trackEvent("cta_click", {
          form_id: "signup_flow",
          ...(source ? { cta_source: source } : {}),
        })
        void navigate("/signup")
        return
      }
      trackEvent("cta_click", {
        form_id: "get_started_choice",
        ...(source ? { cta_source: source } : {}),
      })
      setIsChoiceOpen(true)
    },
    [leadForm],
  )

  const closeChoice = React.useCallback(() => setIsChoiceOpen(false), [])

  const value = React.useMemo(
    () => ({ isChoiceOpen, open, closeChoice }),
    [isChoiceOpen, open, closeChoice],
  )

  return <GetStartedContext.Provider value={value}>{children}</GetStartedContext.Provider>
}

export const useGetStarted = (): GetStartedContextType => {
  const ctx = React.useContext(GetStartedContext)
  if (!ctx) {
    throw new Error("useGetStarted must be used within GetStartedProvider")
  }
  return ctx
}
